import { getSessionToken } from './monday';

async function getAuthHeaders() {
  const sessionToken = await getSessionToken();

  return {
    Authorization: `${sessionToken}`,
  };
}

export async function handleFetchErrorResponse(message: string, res: Response) {
  if (!res.ok) {
    let body = '';

    // Attach extra info to the error object.
    try {
      body = await res.text();
    } catch (err) {}

    // the /widgets routes answer with `{ error: '...' }`, and that text is the whole point of
    // guards like the widget limit or "unpublish this widget before deleting it" - it is what the
    // toast should say, so it wins over the generic message the caller passes in
    let serverMessage: string | undefined;

    try {
      serverMessage = (JSON.parse(body) as { error?: string })?.error;
    } catch (err) {}

    const error = new Error(serverMessage || message);

    (error as any).info = body;
    (error as any).status = res.status;

    throw error;
  }
}

export async function unpublishWidget(path: string) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_PUBLISHER_ENDPOINT_URL}/widget/unpublish`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ path }),
  });

  handleFetchErrorResponse('Failed to unpublish widget', response);

  return await response.text();
}

export type WidgetSummary = {
  id: string;
  name: string | null;
  path: string;
  isDefault: boolean;
  createdAt: string;
};

export async function listWidgets(boardId: number | string) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_APP_BACKEND_URL}/widgets?board_id=${boardId}`, {
    method: 'GET',
    headers: { ...authHeaders, 'content-type': 'application/json' },
  });

  await handleFetchErrorResponse('Failed to load widgets', response);

  return ((await response.json()) as { widgets: WidgetSummary[] }).widgets;
}

export async function createWidget(body: { boardId: number | string; name: string; data?: any; copyFromId?: string }) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_APP_BACKEND_URL}/widgets`, {
    method: 'POST',
    headers: { ...authHeaders, 'content-type': 'application/json' },
    body: JSON.stringify({ ...body, boardId: String(body.boardId) }),
  });

  await handleFetchErrorResponse('Failed to create widget', response);

  return (await response.json()) as WidgetSummary;
}

export async function deleteWidget(id: string) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_APP_BACKEND_URL}/widgets/${id}`, {
    method: 'DELETE',
    headers: { ...authHeaders, 'content-type': 'application/json' },
  });

  await handleFetchErrorResponse('Failed to delete widget', response);

  return (await response.json()) as { id: string };
}

export async function getWidgetsInfo(paths: string[]) {
  if (!paths.length) {
    return [];
  }

  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_PUBLISHER_ENDPOINT_URL}/widget/batch-info`, {
    method: 'POST',
    headers: { ...authHeaders, 'content-type': 'application/json' },
    body: JSON.stringify({ paths }),
  });

  await handleFetchErrorResponse('Failed to get widget info', response);

  return ((await response.json()) as { widgets: Array<{ path: string; id: string; publishedAt: number | undefined }> }).widgets;
}

export async function getWidgetInfo(path: string) {
  const authHeaders = await getAuthHeaders();

  const response = await fetch(`${import.meta.env.VITE_PUBLISHER_ENDPOINT_URL}/widget/info`, {
    method: 'POST',
    headers: {
      ...authHeaders,
      'content-type': 'application/json',
    },
    body: JSON.stringify({ path }),
  });

  handleFetchErrorResponse('Failed to get widget info', response);

  return (await response.json()) as { id: string; publishedAt: number | undefined };
}
